import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Hammer, Save, Send, SlidersHorizontal, Trash2 } from "lucide-react";
import Axios, { authHeaders } from "../../utils/axios";
import { useToast } from "../../context/TostContext";
import { ImportStatusChip, ROLES, WORKING, formatDate } from "./shared";
import PreviewMap from "./PreviewMap";
import CustomSelect from "../../components/form/CustomSelect";

/**
 * One GIS import: match the PDF's layers and colours, build a draft, preview
 * it on the map, publish. See GisImport/index.js for the overview.
 *
 * The admin's choices are kept as UI state (roles per layer, a code per
 * colour, label sizes) and turned into the backend's config shape on build —
 * the same shape as R-Point scripts/gis/configs/*.json.
 */

const POLL_MS = 2000;

/** config (backend) → UI state */
function stateFromConfig(config, analysis) {
  const roles = {};
  for (const layer of analysis?.layers || []) roles[layer.name] = { role: "ignore" };
  const set = (names, role, extra = {}) => (names || []).forEach((n) => (roles[n] = { role, ...extra }));
  set(config?.dp_zones?.layers, "dp_zones");
  set(config?.village_boundaries?.layers, "village_boundaries");
  set(config?.survey_parcels?.layers, "survey_parcels");
  (config?.tp_plots || []).forEach((t) => set(t.layers, "tp_plots", { tp_name: t.tp_name }));
  (config?.roads || []).forEach((r) => set(r.layers, "roads", { width_m: r.width_m ?? "" }));

  const legend = {};
  for (const l of config?.legend || []) legend[l.fill] = { code: l.code || "", name: l.name || "" };

  const size = (band) => (Array.isArray(band) ? String(Math.round(((band[0] + band[1]) / 2) * 10) / 10) : "");
  return {
    authority: config?.plan?.authority || "",
    planName: config?.plan?.name || "",
    roles,
    legend,
    villageFont: size(config?.village_boundaries?.label_font),
    surveyFont: size(config?.survey_parcels?.label_font),
    gap: config?.village_boundaries?.dashed_gap_m ?? 6,
    mergePieces: !!config?.dp_zones?.merge_pieces,
  };
}

/** UI state → config (backend) */
function configFromState(s) {
  const layersWith = (role) => Object.entries(s.roles).filter(([, r]) => r.role === role);
  const band = (size) => (size === "" ? null : [Number(size) - 0.5, Number(size) + 0.5]);
  return {
    plan: { authority: s.authority, name: s.planName },
    legend: Object.entries(s.legend).map(([fill, l]) => ({ fill, code: l.code, name: l.name })),
    dp_zones: { layers: layersWith("dp_zones").map(([n]) => n), merge_pieces: !!s.mergePieces },
    village_boundaries: {
      layers: layersWith("village_boundaries").map(([n]) => n),
      dashed_gap_m: Number(s.gap) || 0,
      label_font: band(s.villageFont),
    },
    survey_parcels: { layers: layersWith("survey_parcels").map(([n]) => n), label_font: band(s.surveyFont) },
    tp_plots: layersWith("tp_plots").map(([n, r]) => ({ tp_name: r.tp_name || "TP", layers: [n] })),
    roads: layersWith("roads").map(([n, r]) => ({ layers: [n], width_m: r.width_m === "" ? null : Number(r.width_m) })),
  };
}

const fmt = (n) => (n == null ? "-" : Number(n).toLocaleString("en-IN"));

/** An import config as profile settings: one exact rule per chosen layer (the server normalises it). */
function profileSettingsFromConfig(config) {
  const rules = [];
  for (const n of config.dp_zones.layers) rules.push({ match: "exact", value: n, role: "dp_zones" });
  for (const n of config.village_boundaries.layers) rules.push({ match: "exact", value: n, role: "village_boundaries" });
  for (const n of config.survey_parcels.layers) rules.push({ match: "exact", value: n, role: "survey_parcels" });
  for (const t of config.tp_plots) for (const n of t.layers) rules.push({ match: "exact", value: n, role: "tp_plots", tp_name: t.tp_name });
  for (const r of config.roads) for (const n of r.layers) rules.push({ match: "exact", value: n, role: "roads", width_m: r.width_m });
  return {
    layer_rules: rules,
    legend: config.legend,
    labels: { village_font: config.village_boundaries.label_font, survey_font: config.survey_parcels.label_font },
    options: {
      dashed_gap_m: config.village_boundaries.dashed_gap_m,
      merge_zone_pieces: config.dp_zones.merge_pieces ? "always" : "auto",
    },
  };
}

/**
 * What to look at before publishing, from the draft's counts and what was read
 * off the sheet. Each says where to fix it; none blocks publishing.
 */
function draftChecks(row) {
  const s = row.summary || {};
  const a = row.analysis || {};
  const out = [];
  const pct = (n, d) => Math.round((100 * n) / d);
  if (!s.dp_zones) {
    out.push("No zoning was built. In the settings, set the layer with the coloured zones to \"Zoning\".");
  } else if (s.uncoded_colours?.length) {
    out.push(
      `${s.uncoded_colours.length} zone colour${s.uncoded_colours.length > 1 ? "s have" : " has"} no zone code, so those zones show as "not identified". Give them codes in the settings (zone colours).`
    );
  }
  const numbers = (a.text || []).filter((t) => t.numeric_like >= 50 && t.inside_map >= 50);
  if (!s.survey_parcels && !s.tp_plots && numbers.length) {
    out.push(
      "The PDF has plot numbers, but no survey parcels were built. In the settings, set the layer with the plot lines (not the numbers) to \"Survey parcels\"."
    );
  } else if (s.survey_parcels >= 20 && s.survey_parcels_numbered / s.survey_parcels < 0.7) {
    out.push(
      `Only ${pct(s.survey_parcels_numbered, s.survey_parcels)}% of survey parcels have a number. Check the survey number text size in the settings (labels).`
    );
  }
  if (s.tp_plots >= 20 && s.tp_plots_numbered / s.tp_plots < 0.7) {
    out.push(
      `Only ${pct(s.tp_plots_numbered, s.tp_plots)}% of TP plots have a number. The TP layer may be the wrong one (hatching or road lines instead of plot outlines); check it in the settings.`
    );
  }
  if (a.fit?.rms_m > 20) {
    out.push(`The control points fit to ${Math.round(a.fit.rms_m)} m. Check the placement against the satellite image; fix the georeference if it is off.`);
  }
  return out;
}

function geometrySummary(layer) {
  return layer.geometries.map((g) => `${fmt(g.n)} ${g.gtype.toLowerCase().replace("multi", "multi-")}`).join(", ");
}

export default function GisImportDetail() {
  const { importId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [row, setRow] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [saveAs, setSaveAs] = useState(null); // name being typed for "save as profile"
  const [settingsOpen, setSettingsOpen] = useState(null); // null: open only while there is no draft yet

  useEffect(() => {
    Axios.get("/maps/gis-imports/profiles", authHeaders())
      .then((res) => setProfiles(res.data?.data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await Axios.get(`/maps/gis-imports/${importId}`, authHeaders());
      const data = res.data?.data;
      setRow(data);
      setLoadError(null);
      // Seed the form once the analysis exists, and again after a build so it
      // shows what was actually built.
      setForm((current) => {
        const stale = !current || (current.seededAt !== data?.updated_at && !WORKING.includes(data?.status));
        return data?.analysis && stale
          ? { ...stateFromConfig(data.config, data.analysis), seededAt: data.updated_at }
          : current;
      });
      return data;
    } catch (err) {
      setLoadError(err.response?.data?.message || "Could not load the import");
      return null;
    }
  }, [importId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while a job runs.
  useEffect(() => {
    if (!row || !WORKING.includes(row.status)) return undefined;
    const timer = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer);
  }, [row, load]);

  const zoneColours = useMemo(() => {
    if (!row?.analysis || !form) return [];
    const merged = new Map();
    for (const layer of row.analysis.layers) {
      if (form.roles[layer.name]?.role !== "dp_zones") continue;
      for (const c of layer.colours) {
        if (!c.fill) continue;
        const prev = merged.get(c.fill) || { fill: c.fill, n: 0, hectares: 0 };
        merged.set(c.fill, { ...prev, n: prev.n + c.n, hectares: prev.hectares + (c.hectares || 0) });
      }
    }
    return [...merged.values()].sort((a, b) => b.hectares - a.hectares);
  }, [row, form]);

  const textOptions = useMemo(
    () =>
      (row?.analysis?.text || [])
        .filter((t) => t.inside_map > 0)
        .map((t) => ({
          value: String(t.size),
          label: `${t.size} pt · ${fmt(t.n)} labels · e.g. ${t.samples.slice(0, 4).join(", ")}`,
        })),
    [row]
  );

  const setRole = (name, patch) =>
    setForm((f) => ({ ...f, roles: { ...f.roles, [name]: { ...f.roles[name], ...patch } } }));
  const setLegend = (fill, patch) =>
    setForm((f) => ({ ...f, legend: { ...f.legend, [fill]: { code: "", name: "", ...f.legend[fill], ...patch } } }));

  const build = async () => {
    setBusy("build");
    try {
      // Only colours of the chosen zoning layers are sent; others stay out of the legend.
      const chosen = new Set(zoneColours.map((c) => c.fill));
      const config = configFromState({
        ...form,
        legend: Object.fromEntries(Object.entries(form.legend).filter(([fill]) => chosen.has(fill))),
      });
      await Axios.post(`/maps/gis-imports/${importId}/build`, { config }, authHeaders());
      setForm((f) => ({ ...f, seededAt: null }));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not build the draft");
    } finally {
      setBusy(null);
    }
  };

  const applyProfile = async () => {
    setBusy("profile");
    try {
      await Axios.post(`/maps/gis-imports/${importId}/apply-profile`, { profile_id: profileId }, authHeaders());
      setForm((f) => ({ ...f, seededAt: null }));
      await load();
      toast.success("Profile applied. Check the settings, then build the draft.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not apply the profile");
    } finally {
      setBusy(null);
    }
  };

  const saveAsProfile = async () => {
    const name = (saveAs || "").trim();
    if (!name) return;
    setBusy("saveas");
    try {
      // From the settings on screen (they may not be built yet), not the saved ones.
      const chosen = new Set(zoneColours.map((c) => c.fill));
      const config = configFromState({
        ...form,
        legend: Object.fromEntries(Object.entries(form.legend).filter(([fill]) => chosen.has(fill))),
      });
      const res = await Axios.post(
        "/maps/gis-imports/profiles",
        { name, authority: form.authority, settings: profileSettingsFromConfig(config) },
        authHeaders()
      );
      setSaveAs(null);
      toast.success(`Profile "${name}" saved.`);
      navigate(`/gis-imports/profiles/${res.data?.data?.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save the profile");
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    setConfirm(null);
    setBusy("publish");
    try {
      await Axios.post(`/maps/gis-imports/${importId}/publish`, {}, authHeaders());
      toast.success("Published. The layers are on the map.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not publish");
    } finally {
      setBusy(null);
    }
  };

  const discard = async () => {
    setConfirm(null);
    setBusy("discard");
    try {
      await Axios.delete(`/maps/gis-imports/${importId}`, authHeaders());
      toast.success("Import discarded.");
      navigate("/gis-imports");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not discard");
      setBusy(null);
    }
  };

  if (loadError) {
    return (
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <Alert variant="danger" className="mb-0">{loadError}</Alert>
      </div>
    );
  }
  if (!row) {
    return (
      <div className="bg-white rounded-2 common-shadow mt-4 p-4 text-center">
        <Spinner />
      </div>
    );
  }

  const working = WORKING.includes(row.status);
  const editable = form && ["ready", "built", "failed"].includes(row.status) && row.analysis;
  const showSettings = editable && (settingsOpen ?? row.status !== "built");
  const checks = row.status === "built" ? draftChecks(row) : [];
  const s = row.summary || {};
  const live = row.live_counts || {};
  const hasLive = Object.values(live).some((n) => n > 0);
  const uncodedInForm = zoneColours.filter((c) => !form?.legend[c.fill]?.code);

  return (
    <>
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <Button
          variant="link"
          className="p-0 d-flex align-items-center gap-1 mb-3"
          onClick={() => navigate(row.batch_id ? `/gis-imports/batches/${row.batch_id}` : "/gis-imports")}
        >
          <ArrowLeft size={16} /> {row.batch_id ? "Back to the bulk import" : "All GIS imports"}
        </Button>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
          <h5 className="fw-semibold mb-0">{row.map?.title || "Sheet"}</h5>
          <ImportStatusChip status={row.status} />
        </div>
        <div className="text-muted small mb-3">
          {row.map?.location_name} · {row.map?.map_type_code?.toUpperCase()} · started {formatDate(row.created_at)}
          {row.analysis?.crs && <> · {row.analysis.crs}</>}
          {row.published_at && <> · published {formatDate(row.published_at)}</>}
        </div>

        {working && (
          <Alert variant="info" className="d-flex align-items-center gap-2 mb-0">
            <Spinner size="sm" /> {row.stage || "Working"}...
          </Alert>
        )}
        {row.status === "failed" && <Alert variant="danger" className="mb-0">{row.error}</Alert>}
        {row.error && row.status !== "failed" && <Alert variant="warning" className="mb-0">{row.error}</Alert>}
        {hasLive && row.status !== "published" && (
          <Alert variant="light" className="small mb-0 mt-2">
            This sheet already has live layers ({fmt(live.dp_zones)} zones, {fmt(live.survey_parcels)} parcels). Publishing
            replaces them; until then they stay as they are.
          </Alert>
        )}
      </div>

      {showSettings && (
        <div className="bg-white rounded-2 common-shadow mt-4 p-4">
          <h6 className="fw-semibold mb-1 d-flex align-items-center gap-2">
            <SlidersHorizontal size={16} /> Profile
          </h6>
          <p className="text-muted small mb-3">
            {row.profile_id
              ? (() => {
                  const p = profiles.find((x) => x.id === row.profile_id);
                  return (
                    <>
                      These settings come from{" "}
                      <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => navigate(`/gis-imports/profiles/${row.profile_id}`)}>
                        {p?.name || "a profile"}
                      </Button>{" "}
                      v{row.profile_version}
                      {p && p.version !== row.profile_version ? ` (the profile is now v${p.version} — apply it again to update)` : ""}
                      . Changes below apply to this sheet only.
                    </>
                  );
                })()
              : "These settings were detected on this sheet. Apply one of your profiles instead, or save these as a new profile."}
          </p>
          <div className="d-flex flex-wrap align-items-end gap-2" style={{ maxWidth: 900 }}>
            <div style={{ minWidth: 320, flex: 1 }}>
              <CustomSelect
                options={profiles
                  .filter((p) => !form?.authority || p.authority === form.authority)
                  .map((p) => ({ value: p.id, label: `${p.name} (${p.authority}) · v${p.version}` }))}
                value={(() => {
                  const p = profiles.find((x) => x.id === profileId);
                  return p ? { value: p.id, label: `${p.name} (${p.authority}) · v${p.version}` } : null;
                })()}
                onChange={(o) => setProfileId(o?.value || "")}
                placeholder={`A ${form?.authority || ""} profile`}
                noOptionsMessage={() => `No ${form?.authority || ""} profile yet`}
                isClearable
              />
            </div>
            <Button variant="outline-primary" disabled={!profileId || !!busy} onClick={applyProfile}>
              {busy === "profile" ? <Spinner size="sm" /> : "Apply profile"}
            </Button>
            <Button variant="outline-secondary" className="d-flex align-items-center gap-1" disabled={!!busy} onClick={() => setSaveAs(`${form?.planName || row.plan_name || ""}`)}>
              <Save size={14} /> Save as new profile
            </Button>
          </div>
        </div>
      )}

      {showSettings && (
        <>
          <div className="bg-white rounded-2 common-shadow mt-4 p-4">
            <h6 className="fw-semibold mb-3">1. Plan</h6>
            <div className="row g-3" style={{ maxWidth: 760 }}>
              <div className="col-md-4">
                <Form.Label className="small fw-medium">Authority</Form.Label>
                <Form.Control
                  value={form.authority}
                  maxLength={30}
                  onChange={(e) => setForm((f) => ({ ...f, authority: e.target.value.toUpperCase() }))}
                  placeholder="e.g. BUDA"
                />
              </div>
              <div className="col-md-8">
                <Form.Label className="small fw-medium">Plan name</Form.Label>
                <Form.Control
                  value={form.planName}
                  onChange={(e) => setForm((f) => ({ ...f, planName: e.target.value }))}
                  placeholder="e.g. Bardoli DP 2037"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2 common-shadow mt-4 p-4">
            <h6 className="fw-semibold mb-1">2. Layers in the PDF</h6>
            <p className="text-muted small mb-3">
              Suggestions come from the layer names. Leave map furniture (grids, insets, outlines) as "Don't import".
            </p>
            <Table responsive size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th>PDF layer</th>
                  <th>Contents</th>
                  <th>Colours</th>
                  <th style={{ minWidth: 220 }}>Import as</th>
                  <th style={{ minWidth: 120 }} />
                </tr>
              </thead>
              <tbody>
                {row.analysis.layers.map((layer) => {
                  const r = form.roles[layer.name] || { role: "ignore" };
                  return (
                    <tr key={layer.name} className={r.role === "ignore" ? "text-muted" : ""}>
                      <td className="small fw-medium text-break">{layer.name}</td>
                      <td className="small">{layer.features ? geometrySummary(layer) : "empty"}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {layer.colours.slice(0, 10).map((c) => (
                            <span
                              key={c.fill}
                              title={`${c.fill} · ${fmt(c.n)} shapes`}
                              style={{ width: 14, height: 14, borderRadius: 3, background: c.fill, border: "1px solid rgba(0,0,0,.25)" }}
                            />
                          ))}
                        </div>
                      </td>
                      <td>
                        <Form.Select size="sm" value={r.role} onChange={(e) => setRole(layer.name, { role: e.target.value })}>
                          {ROLES.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        {r.role === "tp_plots" && (
                          <Form.Control
                            size="sm"
                            value={r.tp_name || ""}
                            placeholder="TP name"
                            onChange={(e) => setRole(layer.name, { tp_name: e.target.value })}
                          />
                        )}
                        {r.role === "roads" && (
                          <Form.Control
                            size="sm"
                            type="number"
                            min={0}
                            value={r.width_m ?? ""}
                            placeholder="Width (m)"
                            onChange={(e) => setRole(layer.name, { width_m: e.target.value })}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="bg-white rounded-2 common-shadow mt-4 p-4">
            <h6 className="fw-semibold mb-1">3. Zone colours</h6>
            <p className="text-muted small mb-3">
              Every fill colour in the zoning layers, largest first. Give each the zone code from the sheet's legend.
              Codes are remembered for {form.authority || "this authority"} and filled in next time.
            </p>
            {zoneColours.length === 0 ? (
              <p className="text-muted small mb-0">No layer is set to "Zoning", or the zoning layers have no filled shapes.</p>
            ) : (
              <Table responsive size="sm" className="align-middle mb-0" style={{ maxWidth: 900 }}>
                <thead>
                  <tr>
                    <th>Colour</th>
                    <th className="text-end">Shapes</th>
                    <th className="text-end">Area</th>
                    <th style={{ width: 140 }}>Zone code</th>
                    <th>Zone name</th>
                  </tr>
                </thead>
                <tbody>
                  {zoneColours.map((c) => (
                    <tr key={c.fill}>
                      <td>
                        <span className="d-inline-flex align-items-center gap-2 small">
                          <span style={{ width: 22, height: 16, borderRadius: 3, background: c.fill, border: "1px solid rgba(0,0,0,.25)" }} />
                          <code>{c.fill}</code>
                        </span>
                      </td>
                      <td className="text-end small">{fmt(c.n)}</td>
                      <td className="text-end small">{fmt(Math.round(c.hectares))} ha</td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={form.legend[c.fill]?.code || ""}
                          isInvalid={!form.legend[c.fill]?.code}
                          placeholder="e.g. R1"
                          maxLength={30}
                          onChange={(e) => setLegend(c.fill, { code: e.target.value.toUpperCase() })}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={form.legend[c.fill]?.name || ""}
                          placeholder="e.g. Residential Zone"
                          onChange={(e) => setLegend(c.fill, { name: e.target.value })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {zoneColours.length > 0 && (
              <Form.Check
                className="mt-3 small"
                type="checkbox"
                id="merge-zone-pieces"
                checked={!!form.mergePieces}
                onChange={(e) => setForm((f) => ({ ...f, mergePieces: e.target.checked }))}
                label={
                  <>
                    Merge touching pieces of the same colour into one zone{" "}
                    <span className="text-muted">
                      — for sheets that fill zones with a mesh of triangles or strips (the preview shows lines across
                      every zone)
                      {row.analysis?.layers?.some((l) => form.roles[l.name]?.role === "dp_zones" && l.triangle_share > 0.5)
                        ? ". This sheet's zoning is drawn that way."
                        : ""}
                    </span>
                  </>
                }
              />
            )}
            {uncodedInForm.length > 0 && (
              <div className="small text-warning mt-2">
                {uncodedInForm.length} colour{uncodedInForm.length > 1 ? "s have" : " has"} no code. Those shapes are
                still drawn, as "zone not identified".
              </div>
            )}
          </div>

          <div className="bg-white rounded-2 common-shadow mt-4 p-4">
            <h6 className="fw-semibold mb-1">4. Labels</h6>
            <p className="text-muted small mb-3">
              Names and numbers are read from the PDF's text. Pick the text size each kind of label uses on this sheet.
            </p>
            <div className="row g-3" style={{ maxWidth: 900 }}>
              <div className="col-md-6">
                <Form.Label className="small fw-medium">Village names</Form.Label>
                <Form.Select
                  size="sm"
                  value={form.villageFont}
                  onChange={(e) => setForm((f) => ({ ...f, villageFont: e.target.value }))}
                >
                  <option value="">None</option>
                  {textOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6">
                <Form.Label className="small fw-medium">Survey numbers</Form.Label>
                <Form.Select
                  size="sm"
                  value={form.surveyFont}
                  onChange={(e) => setForm((f) => ({ ...f, surveyFont: e.target.value }))}
                >
                  <option value="">None</option>
                  {textOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6">
                <Form.Label className="small fw-medium">Close gaps in dashed village lines (m)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  max={50}
                  value={form.gap}
                  onChange={(e) => setForm((f) => ({ ...f, gap: e.target.value }))}
                />
                <Form.Text muted>Raise it if villages come out missing; 0 for solid lines.</Form.Text>
              </div>
            </div>

            <Button className="mt-4 d-flex align-items-center gap-2" onClick={build} disabled={!!busy || working}>
              {busy === "build" ? <Spinner size="sm" /> : <Hammer size={16} />}
              {row.status === "built" ? "Rebuild the draft" : "Build the draft"}
            </Button>
          </div>
        </>
      )}

      {(row.status === "built" || row.status === "published") && (
        <div className="bg-white rounded-2 common-shadow mt-4 p-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <h6 className="fw-semibold mb-0">
              {row.status === "published" ? "Published layers" : `${showSettings ? "5. " : ""}Draft — check it, then publish`}
            </h6>
            {editable && (
              <Button
                variant={showSettings ? "light" : "outline-secondary"}
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={() => setSettingsOpen(!showSettings)}
              >
                <SlidersHorizontal size={14} /> {showSettings ? "Hide settings" : "Adjust settings"}
              </Button>
            )}
          </div>

          <div className="d-flex flex-wrap gap-4 mb-3 small">
            <Stat label="Zones" value={s.dp_zones} sub={`${fmt(s.dp_zones_coded)} with a code`} />
            <Stat label="Villages" value={s.villages} sub={`${fmt(s.villages_named)} named`} />
            <Stat label="Survey parcels" value={s.survey_parcels} sub={`${fmt(s.survey_parcels_numbered)} numbered`} />
            <Stat label="TP plots" value={s.tp_plots} sub={`${fmt(s.tp_plots_numbered)} numbered`} />
            <Stat label="Roads" value={s.roads} />
          </div>

          {row.status === "built" &&
            (checks.length ? (
              <Alert variant="warning" className="small py-2">
                <div className="fw-medium mb-1">Check before publishing</div>
                <ul className="mb-0 ps-3">
                  {checks.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </Alert>
            ) : (
              <div className="small text-success d-flex align-items-center gap-1 mb-2">
                <CheckCircle2 size={14} /> Zones are coded and parcels are numbered. Compare the preview with the PDF, then publish.
              </div>
            ))}

          <PreviewMap importId={importId} version={row.updated_at} bounds={s.bounds} />

          {row.status === "built" && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              <Button variant="success" className="d-flex align-items-center gap-2" disabled={!!busy} onClick={() => setConfirm("publish")}>
                {busy === "publish" ? <Spinner size="sm" /> : <Send size={16} />}
                Publish to the map
              </Button>
            </div>
          )}
        </div>
      )}

      {!working && (
        <div className="bg-white rounded-2 common-shadow mt-4 p-4 d-flex align-items-center justify-content-between gap-3">
          <span className="text-muted small">
            {row.status === "published"
              ? "Removing this record keeps the published layers. To change them, start a new import of the sheet."
              : "Discarding removes the draft and the staged layers. Live layers are not touched."}
          </span>
          <Button variant="outline-danger" size="sm" className="d-flex align-items-center gap-2" disabled={!!busy} onClick={() => setConfirm("discard")}>
            {busy === "discard" ? <Spinner size="sm" /> : <Trash2 size={14} />}
            {row.status === "published" ? "Remove record" : "Discard import"}
          </Button>
        </div>
      )}

      <Modal show={saveAs !== null} onHide={() => setSaveAs(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Save these settings as a profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label className="small fw-medium">Profile name</Form.Label>
          <Form.Control value={saveAs || ""} onChange={(e) => setSaveAs(e.target.value)} placeholder="e.g. GIFT City DP 2024" autoFocus />
          <Form.Text muted>
            Layer choices become rules matching these layer names exactly; edit the profile afterwards to widen them.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setSaveAs(null)}>Cancel</Button>
          <Button onClick={saveAsProfile} disabled={!saveAs?.trim() || busy === "saveas"}>
            {busy === "saveas" ? <Spinner size="sm" /> : "Save profile"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!confirm} onHide={() => setConfirm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{confirm === "publish" ? "Publish these layers?" : "Discard this import?"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {confirm === "publish"
            ? `Users with GIS access will see this draft on the map${hasLive ? ", replacing the sheet's current layers" : ""}.`
            : row.status === "published"
              ? "The record is removed. The published layers stay on the map."
              : "The draft and the staged layers are deleted. You can start again from the sheet."}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setConfirm(null)}>Cancel</Button>
          {confirm === "publish" ? (
            <Button variant="success" onClick={publish}>Publish</Button>
          ) : (
            <Button variant="danger" onClick={discard}>{row.status === "published" ? "Remove" : "Discard"}</Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-muted">{label}</div>
      <div className="fs-5 fw-semibold">{fmt(value)}</div>
      {sub && <div className="text-muted">{sub}</div>}
    </div>
  );
}
